import { CustomCellEditorProps } from 'ag-grid-react';
import { parseNumberWithAccelerator } from 'components/shared/Blotter/Grid/Hooks/number-accelerator';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isPrintableKey } from 'utils/aggrid';

const getInitialDisplayValue = ({
  eventKey,
  initialValue,
}: Pick<CustomCellEditorProps<unknown, number | undefined>, 'eventKey' | 'initialValue'>) => {
  if (eventKey === 'Backspace') {
    return '';
  }

  if (isPrintableKey(eventKey)) {
    return eventKey;
  }

  return String(initialValue ?? '');
};

type Props<Data = unknown> = { placeholder?: string; readOnly?: boolean } & CustomCellEditorProps<
  Data,
  number | undefined
>;
export function NumberAcceleratorCellEditor<Data = unknown>({
  onValueChange,
  eventKey,
  initialValue,
  readOnly,
  placeholder,
  value,
}: Props<Data>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [displayValue, setDisplayValue] = useState(() => getInitialDisplayValue({ eventKey, initialValue }));
  const focusInput = useCallback(() => {
    const input = inputRef.current;

    if (input === null) {
      return;
    }

    input.focus();
  }, []);

  useEffect(() => {
    setDisplayValue(getInitialDisplayValue({ eventKey, initialValue }));
  }, [initialValue, eventKey]);

  useEffect(() => {
    if (readOnly) {
      return;
    }
    focusInput();
  }, [focusInput, readOnly]);

  useEffect(() => {
    if (readOnly) {
      return;
    }
    const newValue = parseNumberWithAccelerator(displayValue);
    if (value !== newValue) {
      onValueChange(newValue);
    }
  }, [displayValue, onValueChange, value, readOnly]);

  const parseAndFormat = (value: string): string => {
    const parsedValue = parseNumberWithAccelerator(value);
    return parsedValue === undefined ? value : String(parsedValue);
  };

  const handleChange = (value: string) => {
    setDisplayValue(parseAndFormat(value));
  };

  return (
    <input
      onClick={readOnly ? e => e.preventDefault() : undefined}
      ref={inputRef}
      readOnly={readOnly}
      className="ag-input-field-input ag-text-field-input"
      value={displayValue}
      placeholder={placeholder}
      onChange={event => {
        handleChange(event.target.value);
      }}
    />
  );
}
